import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AudioElement,
    ControllableElement,
    MultiPageImageModel,
    MultiPageVideoModel,
} from '@bynder-studio/render-core';
import { List, Thumbnail } from '@bynder/design-system';
import { IconShow, IconVolumeUp } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import generateTestId from '~/helpers/testIdHelpers';
import useVisibilityControlsShortcuts from 'packages/hooks/shortcuts/useVisibilityControlsShortcuts';

type Props = {
    selectedElement: ControllableElement;
    creativeModel: MultiPageVideoModel | MultiPageImageModel;
    disabled?: boolean;
};

const AllowToggleVisibility = ({ selectedElement, creativeModel, disabled = false }: Props) => {
    const { locked, id } = selectedElement;
    const { translate } = useTranslate();
    const [visibility, setVisibility] = useState(selectedElement.allowToggleVisibility);
    const testId = useMemo(() => generateTestId('shots_properties_visibility_control'), []);

    const isAudioElement = selectedElement instanceof AudioElement;

    const onToggle = useCallback(() => {
        const newVisibility = !visibility;

        creativeModel.updateElement(id, { allowToggleVisibility: newVisibility });
        setVisibility(newVisibility);
    }, [creativeModel, id, visibility]);

    useEffect(() => {
        setVisibility(selectedElement.allowToggleVisibility);
    }, [selectedElement.allowToggleVisibility]);

    useVisibilityControlsShortcuts({ toggleVisibility: onToggle });

    return (
        <List.Item
            checkedVariant="switch"
            onClick={onToggle}
            isChecked={visibility}
            isDisabled={locked || disabled}
            thumbnail={<Thumbnail icon={isAudioElement ? <IconVolumeUp /> : <IconShow />} />}
            {...testId}
        >
            {isAudioElement
                ? translate('editor.sidebar.shots.audio_control.label')
                : translate('editor.sidebar.shots.visibility_control.label')}
        </List.Item>
    );
};

export default AllowToggleVisibility;
